import {
  Alert,
  AlertDescription,
  AlertIcon,
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  Text,
  VStack,
} from '@chakra-ui/react'
import { RoundedButton } from '@components/Buttons'
import { FormElement } from '@components/forms/Input'
import { useGynCareView } from '@components/Gyn/context'
import { useAuth } from '@hb/react'
import {
  computeHadlockEfw,
  getEfwPercentile,
  objectToArray,
  UltrasoundDopplerStudy,
  UltrasoundFetalBiometry,
  ultrasoundFindingsField,
  UltrasoundPlacentalLocation,
  UltrasoundScenario,
  ultrasoundScenarioBField,
} from '@hb/shared'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useField, useForm } from 'react-final-form'
import { PracticeProviderSelect } from '../../../../components/Practices/components/PracticeProviderSelect'
import { usePracticeProviders } from '../../../../components/Practices/hooks/usePracticeProviders'
import { SectionLabel } from '../../SectionLabel'

const placentaAlert = (location: UltrasoundPlacentalLocation | undefined) => {
  if (location === 'low-lying')
    return {
      status: 'warning' as const,
      text: 'Low-lying placenta: follow-up ultrasound at 32w recommended; counsel on pelvic rest.',
    }
  if (location === 'previa')
    return {
      status: 'error' as const,
      text: 'Placenta previa: refer to MFM; pelvic rest; counsel on delivery planning.',
    }
  return null
}

const fluidAlert = (type: 'afi' | 'sdp' | undefined, value: number | undefined) => {
  if (!type || value == null) return null
  if (type === 'afi') {
    if (value < 5)
      return {
        status: 'error' as const,
        text: `AFI ${value} cm — oligohydramnios. Consider further evaluation.`,
      }
    if (value > 25)
      return {
        status: 'warning' as const,
        text: `AFI ${value} cm — polyhydramnios. Consider further evaluation.`,
      }
  }
  if (type === 'sdp') {
    if (value < 2)
      return {
        status: 'error' as const,
        text: `SDP ${value} cm — oligohydramnios. Consider further evaluation.`,
      }
    if (value > 8)
      return {
        status: 'warning' as const,
        text: `SDP ${value} cm — polyhydramnios. Consider further evaluation.`,
      }
  }
  return null
}

// §2.14.3: Sonographer sign-off panel — provider select + confirm button.
// PracticeProviderSelect writes the selected userId to data._sonoPickId (transient UI field).
// On confirm, reads that value and writes { providerId, signedAt } to data.sonographerSignOff.
const SonographerSignOffPanel = ({ practiceId }: { practiceId: string | null }) => {
  const form = useForm()
  const [confirming, setConfirming] = useState(false)

  const {
    input: { value: signOff },
  } = useField<{ providerId: string; signedAt: string } | undefined>('data.sonographerSignOff', {
    subscription: { value: true },
  })

  // Reads the transient picker value written by PracticeProviderSelect
  const {
    input: { value: pickedId },
  } = useField<string>('data._sonoPickId', { subscription: { value: true } })

  const handleConfirm = useCallback(async () => {
    if (!pickedId) return
    setConfirming(true)
    form.change('data.sonographerSignOff', {
      providerId: pickedId,
      signedAt: new Date().toISOString(),
    })
    form.change('data._sonoPickId', undefined)
    setConfirming(false)
  }, [form, pickedId])

  const handleClear = useCallback(() => {
    form.change('data.sonographerSignOff', undefined)
  }, [form])

  if (signOff?.providerId) {
    return (
      <Flex
        align="center"
        gap={2}
        bg="green.50"
        border="1px solid"
        borderColor="green.300"
        borderRadius={6}
        px={3}
        py={2}
        _dark={{ bg: 'green.900', borderColor: 'green.600' }}>
        <Text fontSize="sm" fontWeight={600} color="green.700" _dark={{ color: 'green.300' }}>
          Sonographer signed off
        </Text>
        <Button size="xs" colorScheme="gray" variant="ghost" ml="auto" onClick={handleClear}>
          Clear
        </Button>
      </Flex>
    )
  }

  return (
    <Box
      border="1px solid"
      borderColor="orange.300"
      borderRadius={6}
      bg="orange.50"
      _dark={{ bg: 'orange.900', borderColor: 'orange.600' }}
      p={3}>
      <Text
        fontSize="sm"
        fontWeight={600}
        color="orange.700"
        _dark={{ color: 'orange.300' }}
        mb={2}>
        Sonographer Sign-Off Required
      </Text>
      <Text fontSize="xs" color="gray.600" _dark={{ color: 'gray.400' }} mb={2}>
        Select the sonographer and confirm sign-off before the interpreting provider authenticates.
      </Text>
      <Flex gap={2} align="flex-end">
        <Box flex={1}>
          <PracticeProviderSelect
            practiceId={practiceId}
            name="data._sonoPickId"
            placeholder="Select sonographer..."
            style={{ padding: '0.25rem' }}
            inputStyle={{ fontSize: '0.875rem' }}
          />
        </Box>
        <RoundedButton
          size="sm"
          isDisabled={!pickedId}
          isLoading={confirming}
          onClick={handleConfirm}>
          Confirm Sign-Off
        </RoundedButton>
      </Flex>
    </Box>
  )
}

// §2.14.2: POCUS credentialing warning (non-blocking, informational only).
// Reads pocusCredential from the current user's practice record.
const PocusCredentialWarning = ({ practiceId }: { practiceId: string | null }) => {
  const authUserId = useAuth(s => s.authUser?.uid)
  const { data: providers } = usePracticeProviders(practiceId)

  const hasPocusCredential = useMemo(() => {
    if (!authUserId || !providers) return true
    const myRecord = objectToArray(providers).find(p => p.id === authUserId)
    return myRecord?.pocusCredential === true
  }, [authUserId, providers])

  if (hasPocusCredential) return null

  return (
    <Alert status="warning" borderRadius={4}>
      <AlertIcon />
      <AlertDescription fontSize="sm">
        POCUS credential not found in provider profile — billing team will be flagged for review.
      </AlertDescription>
    </Alert>
  )
}

export const UltrasoundFindingsInput = ({ scenario }: { scenario: UltrasoundScenario }) => {
  const form = useForm()
  const { practiceId } = useGynCareView()

  // Write scenario to form data on mount so the sonographerSignOff tier validator can depend on it.
  useEffect(() => {
    form.change('data.scenarioType', scenario)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const {
    input: { value: placentalLocation },
  } = useField<UltrasoundPlacentalLocation | undefined>('data.placentalLocation', {
    subscription: { value: true },
  })

  const {
    input: { value: amnioticFluidType },
  } = useField<'afi' | 'sdp' | undefined>('data.amnioticFluidType', {
    subscription: { value: true },
  })

  const {
    input: { value: amnioticFluidValue },
  } = useField<number | undefined>('data.amnioticFluidValue', {
    subscription: { value: true },
  })

  const {
    input: { value: biometry },
  } = useField<UltrasoundFetalBiometry | undefined>('data.fetalBiometry', {
    subscription: { value: true },
  })

  const {
    input: { value: gaAtStudyDays },
  } = useField<number | undefined>('data.gaAtStudyDays', { subscription: { value: true } })

  const {
    input: { value: dopplerStudy },
  } = useField<UltrasoundDopplerStudy | undefined>('data.dopplerStudy', {
    subscription: { value: true },
  })

  // Auto-calculate EFW from Hadlock formula whenever biometry values change.
  useEffect(() => {
    if (!biometry) return
    const efw = computeHadlockEfw(biometry)
    if (efw !== null) form.change('data.fetalBiometry.efw', efw)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [biometry?.bpd, biometry?.hc, biometry?.ac, biometry?.fl])

  // Auto-calculate CPR (cerebroplacental ratio) from MCA PI / UA PI.
  useEffect(() => {
    if (!dopplerStudy?.mcaPi || !dopplerStudy?.uaPi) return
    const cpr = Math.round((dopplerStudy.mcaPi / dopplerStudy.uaPi) * 100) / 100
    form.change('data.dopplerStudy.cpr', cpr)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dopplerStudy?.mcaPi, dopplerStudy?.uaPi])

  const dopplerAlert = (d: UltrasoundDopplerStudy | undefined) => {
    if (!d) return null
    if (d.uaEdFlow === 'reversed' || d.ductusVenosus === 'reversed-a-wave')
      return {
        status: 'error' as const,
        text: 'Reversed end-diastolic flow or reversed DV a-wave — urgent MFM referral indicated.',
      }
    if (d.uaEdFlow === 'absent' || d.ductusVenosus === 'absent-a-wave')
      return {
        status: 'warning' as const,
        text: 'Absent end-diastolic flow or absent DV a-wave — MFM consultation indicated.',
      }
    if (d.cpr != null && d.cpr < 1.0)
      return {
        status: 'warning' as const,
        text: `CPR ${d.cpr.toFixed(2)} — cerebroplacental redistribution (brain-sparing pattern). Consider MFM referral.`,
      }
    return null
  }

  const efwPercentile = useMemo(() => {
    if (biometry?.efw == null || gaAtStudyDays == null) return null
    return getEfwPercentile(biometry.efw, Math.floor(gaAtStudyDays / 7))
  }, [biometry?.efw, gaAtStudyDays])

  const efwPercentileColor = (pct: number) => {
    if (pct < 5 || pct > 95) return 'red'
    if (pct < 10 || pct > 90) return 'orange'
    return 'green'
  }

  const placenta = placentaAlert(placentalLocation)
  const fluid = fluidAlert(amnioticFluidType, amnioticFluidValue)
  const doppler = dopplerAlert(dopplerStudy)

  return (
    <VStack align="stretch" spacing={3} w="100%" data-child-name="data" px={4} py={3}>
      {/* §2.14.2: POCUS credentialing warning for provider-pocus scenario */}
      {scenario === 'provider-pocus' && <PocusCredentialWarning practiceId={practiceId} />}

      <Box>
        <SectionLabel>Findings</SectionLabel>
        <FormElement root showName={false} field={ultrasoundFindingsField} name="data" />
        {efwPercentile != null && (
          <Flex align="center" gap={2} mt={2}>
            <Text fontSize="xs" color="gray.500" _dark={{ color: 'gray.400' }}>
              EFW Percentile
            </Text>
            <Badge colorScheme={efwPercentileColor(efwPercentile)} fontSize="xs">
              {efwPercentile}th %ile
            </Badge>
          </Flex>
        )}
      </Box>

      {placenta && (
        <Alert status={placenta.status} borderRadius={4}>
          <AlertIcon />
          <AlertDescription fontSize="sm">{placenta.text}</AlertDescription>
        </Alert>
      )}
      {fluid && (
        <Alert status={fluid.status} borderRadius={4}>
          <AlertIcon />
          <AlertDescription fontSize="sm">{fluid.text}</AlertDescription>
        </Alert>
      )}
      {doppler && (
        <Alert status={doppler.status} borderRadius={4}>
          <AlertIcon />
          <AlertDescription fontSize="sm">{doppler.text}</AlertDescription>
        </Alert>
      )}

      {/* §2.14.3: Scenario B — image quality, agreement with findings, sonographer sign-off */}
      {scenario === 'sonographer-study' && (
        <>
          <Divider />
          <Box>
            <Text
              fontSize="sm"
              fontWeight={600}
              color="gray.600"
              _dark={{ color: 'gray.300' }}
              mb={2}>
              Sonographer Study Details
            </Text>
            <FormElement root showName={false} field={ultrasoundScenarioBField} name="data" />
          </Box>
          <SonographerSignOffPanel practiceId={practiceId} />
        </>
      )}
    </VStack>
  )
}
